import React, { useEffect, useState } from "react";
import {
  Box, Grid, Typography, Button, alpha,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import BusinessIcon from "@mui/icons-material/Business";
import GroupsIcon from "@mui/icons-material/Groups";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import SmsIcon from "@mui/icons-material/Sms";
import EmailIcon from "@mui/icons-material/Email";
import ChatIcon from "@mui/icons-material/Chat";
import AddIcon from "@mui/icons-material/Add";
import SendIcon from "@mui/icons-material/Send";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SpaceDashboardRoundedIcon from "@mui/icons-material/SpaceDashboardRounded";
import PageLoader from "../../Components/Loader/PageLoader";
import { AppPage, PageHeader, StatCard, CardSurface } from "../../Components/ui";
import { NAVY, GOLD } from "../../theme/tokens";

const API_URL = (process.env.REACT_APP_API_URL || "").trim();

interface DashboardData {
  companies: number;
  customers: number;
  team: number;
  messages: { sms_total: number; email_total: number };
}

const num = (v: any) => Number(v ?? 0).toLocaleString("en-CA");

const Dashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = localStorage.getItem("token") || "";
    fetch(`${API_URL}/dashboard/summary`, {
      headers: { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${t}` },
    })
      .then(r => r.json())
      .then(json => { if (json.status && json.data) setData(json.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <PageLoader />;

  if (!data) return (
    <AppPage>
      <Box sx={{ textAlign: "center", py: 12 }}>
        <Typography fontSize={18} fontWeight={600} color={NAVY}>Unable to load dashboard</Typography>
        <Typography fontSize={13} color="text.secondary" mt={1}>Please check your connection and try again</Typography>
      </Box>
    </AppPage>
  );

  const d = data;
  const messages = d.messages ?? { sms_total: 0, email_total: 0 };
  const totalMessages = Number(messages.sms_total ?? 0) + Number(messages.email_total ?? 0);

  return (
    <AppPage
      disablePaper
      header={
        <PageHeader
          icon={<SpaceDashboardRoundedIcon />}
          title="Dashboard"
          subtitle="Overview of your communication platform"
          actions={
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                size="small"
                variant="contained"
                disableElevation
                startIcon={<SendIcon sx={{ fontSize: 16 }} />}
                onClick={() => navigate("/sms/logs")}
                sx={{
                  borderRadius: "10px", textTransform: "none", fontSize: 13, fontWeight: 600, px: 2.5, py: 0.85,
                  background: `linear-gradient(135deg, ${NAVY} 0%, #12324A 100%)`,
                  color: "#FFFFFF",
                  border: `1px solid ${alpha(GOLD, 0.35)}`,
                  "&:hover": {
                    background: `linear-gradient(135deg, #071521 0%, ${NAVY} 100%)`,
                  },
                }}
              >
                Send SMS
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<BusinessIcon sx={{ fontSize: 16 }} />}
                onClick={() => navigate("/platform/companies")}
                sx={{
                  borderRadius: "10px", textTransform: "none", fontSize: 13, fontWeight: 600, px: 2, py: 0.85,
                  borderColor: alpha(NAVY, 0.25), color: NAVY,
                  "&:hover": { borderColor: alpha(GOLD, 0.55), bgcolor: alpha(GOLD, 0.05) },
                }}
              >
                Companies
              </Button>
            </Box>
          }
        />
      }
    >
      {/* ── KPI STAT CARDS ─────────────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <StatCard
            icon={<SmsIcon />}
            label="SMS Sent"
            value={num(messages.sms_total)}
            color="#0EA5E9"
            onClick={() => navigate("/sms/logs")}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            icon={<EmailIcon />}
            label="Emails Sent"
            value={num(messages.email_total)}
            color="#8B5CF6"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            icon={<ChatIcon />}
            label="Total Messages"
            value={num(totalMessages)}
            color="#10B981"
            onClick={() => navigate("/sms/logs")}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            icon={<BusinessIcon />}
            label="Companies"
            value={num(d.companies)}
            color={NAVY}
            onClick={() => navigate("/platform/companies")}
          />
        </Grid>
      </Grid>

      {/* ── MAIN CONTENT COLUMNS ───────────────────────────────────────────── */}
      <Grid container spacing={3}>

        {/* ═══ LEFT COLUMN ══════════════════════════════════════════════════ */}
        <Grid item xs={12} lg={8}>

          {/* Quick Nav Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <StatCard
                icon={<BusinessIcon />}
                label="Companies"
                value={num(d.companies)}
                color={NAVY}
                onClick={() => navigate("/platform/companies")}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard
                icon={<PeopleAltIcon />}
                label="Customers"
                value={num(d.customers)}
                color="#8B5CF6"
                onClick={() => navigate("/platform/users")}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard
                icon={<GroupsIcon />}
                label="Team"
                value={num(d.team)}
                color="#F97316"
                onClick={() => navigate("/platform/users")}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard
                icon={<SmsIcon />}
                label="Messages"
                value={num(totalMessages)}
                color="#10B981"
                onClick={() => navigate("/sms/logs")}
              />
            </Grid>
          </Grid>

          {/* Messaging Breakdown */}
          <CardSurface noHover sx={{ overflow: "hidden" }}>
            <Box sx={{ px: 3, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #EEF2F7" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box sx={{
                  width: 30, height: 30, borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center",
                  background: `linear-gradient(135deg, ${NAVY} 0%, #12324A 100%)`,
                  border: `1px solid ${alpha(GOLD, 0.30)}`,
                }}>
                  <ChatIcon sx={{ fontSize: 15, color: GOLD }} />
                </Box>
                <Typography fontSize={15} fontWeight={700} color={NAVY}>Messaging Breakdown</Typography>
              </Box>
              <Button size="small" endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                onClick={() => navigate("/sms/logs")}
                sx={{ textTransform: "none", fontSize: 12, fontWeight: 600, color: NAVY, borderRadius: "8px", "&:hover": { bgcolor: alpha(GOLD, 0.07) } }}>
                View Logs
              </Button>
            </Box>
            <Box sx={{ p: 3 }}>
              {[
                { label: "SMS", value: Number(messages.sms_total ?? 0), color: "#0EA5E9", icon: <SmsIcon /> },
                { label: "Email", value: Number(messages.email_total ?? 0), color: "#8B5CF6", icon: <EmailIcon /> },
              ].map((row, i) => {
                const pct = totalMessages > 0 ? Math.round((row.value / totalMessages) * 100) : 0;
                return (
                  <Box key={i} sx={{ mb: i === 0 ? 2.5 : 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {React.cloneElement(row.icon, { sx: { fontSize: 16, color: row.color } })}
                        <Typography fontSize={13} fontWeight={600} color="#334155">{row.label}</Typography>
                      </Box>
                      <Typography fontSize={13} fontWeight={700} color={NAVY}>{num(row.value)} <Box component="span" sx={{ color: "#94A3B8", fontWeight: 500 }}>({pct}%)</Box></Typography>
                    </Box>
                    <Box sx={{ height: 8, borderRadius: 4, bgcolor: "#F1F5F9", overflow: "hidden" }}>
                      <Box sx={{ height: "100%", width: `${pct}%`, borderRadius: 4, background: alpha(row.color, 0.85), transition: "width 0.4s ease" }} />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </CardSurface>
        </Grid>

        {/* ═══ RIGHT COLUMN ═════════════════════════════════════════════════ */}
        <Grid item xs={12} lg={4}>

          {/* Quick Actions */}
          <CardSurface noHover sx={{ p: 3, mb: 3 }}>
            <Typography fontSize={14} fontWeight={700} color={NAVY} mb={2}>Quick Actions</Typography>
            <Grid container spacing={1.5}>
              {[
                { label: "Send SMS", icon: <SmsIcon />, color: NAVY, path: "/sms/logs" },
                { label: "WhatsApp", icon: <WhatsAppIcon />, color: "#10B981", path: "/whatsapp/settings" },
                { label: "Add Company", icon: <AddIcon />, color: "#8B5CF6", path: "/platform/companies" },
                { label: "Manage Users", icon: <GroupsIcon />, color: "#F97316", path: "/platform/users" },
              ].map((a, i) => (
                <Grid item xs={6} key={i}>
                  <Button
                    fullWidth
                    size="small"
                    disableElevation
                    startIcon={React.cloneElement(a.icon, { sx: { fontSize: 16 } })}
                    onClick={() => navigate(a.path)}
                    sx={{
                      borderRadius: "10px", textTransform: "none", fontSize: 12, fontWeight: 700, py: 1.4,
                      background: alpha(a.color, 0.10),
                      color: a.color,
                      border: `1px solid ${alpha(a.color, 0.20)}`,
                      transition: "all 0.2s ease",
                      "&:hover": {
                        background: alpha(a.color, 0.16),
                        borderColor: alpha(a.color, 0.40),
                        transform: "translateY(-1px)",
                      },
                    }}
                  >
                    {a.label}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </CardSurface>

          {/* Platform Summary */}
          <CardSurface noHover sx={{ p: 3 }}>
            <Typography fontSize={14} fontWeight={700} color={NAVY} mb={2}>Platform Summary</Typography>
            {[
              { label: "Companies", value: Number(d.companies ?? 0), icon: <BusinessIcon />, color: "#0EA5E9" },
              { label: "Customers", value: Number(d.customers ?? 0), icon: <PeopleAltIcon />, color: "#8B5CF6" },
              { label: "Team Members", value: Number(d.team ?? 0), icon: <GroupsIcon />, color: "#F97316" },
              { label: "Total Messages", value: totalMessages, icon: <ChatIcon />, color: "#10B981" },
            ].map((item, i) => (
              <Box key={i} sx={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                py: 1.25, borderBottom: i < 3 ? "1px solid #F1F5F9" : "none",
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box sx={{ width: 30, height: 30, borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: alpha(item.color, 0.10) }}>
                    {React.cloneElement(item.icon, { sx: { fontSize: 15, color: item.color } })}
                  </Box>
                  <Typography fontSize={13} color="text.secondary" fontWeight={500}>{item.label}</Typography>
                </Box>
                <Typography fontSize={13} fontWeight={800} color={NAVY} sx={{ fontVariantNumeric: "tabular-nums" }}>{num(item.value)}</Typography>
              </Box>
            ))}
          </CardSurface>
        </Grid>
      </Grid>
    </AppPage>
  );
};

export default Dashboard;
